import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from 'api/api';
import TokenUtils from 'utils/TokenUtils';
import { addNotification } from './notificationsSlice';

export const getClients = createAsyncThunk('clients/getClients', async (searchQuery, { rejectWithValue }) => {
  try {
    const response = await api.get('/clients', {
      params: { search: searchQuery },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати дані клієнтів');
  }
});

export const getClient = createAsyncThunk('clients/getClient', async (clientID, { rejectWithValue }) => {
  try {
    const response = await api.get(`/clients/${clientID}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати дані клієнта');
  }
});

export const createClient = createAsyncThunk(
  'clients/createClient',
  async (clientData, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.post('/clients', clientData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        dispatch(
          addNotification({
            id: Date.now(),
            title: 'Клієнта додано успішно',
            type: 'success',
          })
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося додати клієнта');
    }
  }
);

export const updateClient = createAsyncThunk(
  'clients/updateClient',
  async (updatedData, { rejectWithValue, dispatch }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.put('/clients', updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Дані клієнта оновлено',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося оновити дані клієнта');
    }
  }
);

export const deleteClients = createAsyncThunk('clients/deleteClients', async (clientIds, { rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();
    const response = await api.delete('/clients', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: { ids: clientIds },
    });

    return response.data?.deletedIds;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося видалити клієнтів');
  }
});

export const getBalanceHistory = createAsyncThunk(
  'clients/getBalanceHistory',
  async (clientID, { rejectWithValue }) => {
    try {
      const response = await api.get(`/clients/${clientID}/balance/history`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати історію балансу');
    }
  }
);

export const adjustBalance = createAsyncThunk(
  'clients/adjustBalance',
  async ({ clientID, ...values }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.post(`/clients/${clientID}/balance/adjust`, values, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Баланс успішно скориговано',
          type: 'success',
        })
      );

      return { clientID, newBalance: response.data.balance };
    } catch (error) {
      const message = error.response?.data?.message || 'Сталася помилка при створенні рахунку';

      dispatch(
        addNotification({
          id: Math.random().toString(36).substr(2, 9),
          title: message,
          type: 'error',
        })
      );

      return rejectWithValue(message);
    }
  }
);

const clientsSlice = createSlice({
  name: 'clients',
  initialState: {
    clients: [],
    balanceHistory: [],
    error: null,
    isLoading: false,
  },
  reducers: {
    updateClientInState: (state, action) => {
      const client = action.payload;
      const index = state.clients.findIndex((c) => c.id === client.id);
      if (index !== -1) {
        state.clients[index] = { ...state.clients[index], ...client };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClients.fulfilled, (state, action) => {
        state.clients = action.payload;
      })
      .addCase(getClients.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося отримати дані клієнтів';
      })
      .addCase(getClient.fulfilled, (state, action) => {
        const existingIndex = state.clients.findIndex((c) => c.id === action.payload.id);

        if (existingIndex !== -1) {
          state.clients[existingIndex] = action.payload;
        } else {
          state.clients.unshift(action.payload);
        }
      })
      .addCase(getClient.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося отримати дані клієнта';
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.clients = [action.payload, ...state.clients];
      })
      .addCase(createClient.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося додати клієнта';
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        const index = state.clients.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося оновити дані клієнта';
      })
      .addCase(deleteClients.fulfilled, (state, action) => {
        state.clients = state.clients.filter((client) => !action.payload.includes(client.id));
      })
      .addCase(deleteClients.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося видалити клієнтів';
      })
      .addCase(getBalanceHistory.fulfilled, (state, action) => {
        state.balanceHistory = action.payload;
      })
      .addCase(getBalanceHistory.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося отримати історію балансу';
      })
      .addCase(adjustBalance.fulfilled, (state, action) => {
        const { clientID, newBalance } = action.payload;
        const client = state.clients.find((c) => c.id === Number(clientID));
        if (client) {
          client.balance = newBalance;
        }
      })
      .addCase(adjustBalance.rejected, (state, action) => {
        state.error = action.payload || 'Помилка коригування балансу';
      })
      .addMatcher(
        (action) => action.type.endsWith('/pending'),
        (state) => {
          state.isLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected'),
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export const { updateClientInState } = clientsSlice.actions;

export default clientsSlice.reducer;
